import React, { useState, useEffect } from "react";
import { Reveal, Overline } from "./Reveal";
import { api, formatApiError } from "@/lib/api";
import { BOOKING_IMG } from "@/data/content";
import { toast } from "sonner";
import { Check, ChevronRight, ChevronLeft, Phone } from "lucide-react";

const STEPS = ["Contact", "Event", "Spaces", "Production", "Budget", "Final"];
const STORAGE_KEY = "epic_booking_draft";

const initial = {
  full_name: "", organization: "", email: "", phone: "", contact_method: "Phone", referral_source: "",
  event_type: "", event_visibility: "Private", event_date: "", alternate_date: "", start_time: "", end_time: "", arrival_time: "", guest_count: "",
  preferred_rooms: [], setup_style: "", needs_stage: "No", stage_use: "", needs_kitchen: "No", needs_prep: "No", alcohol_served: "Undecided", alcohol_type: "",
  production_needs: [], live_entertainment: "No", entertainment_type: "", vendors_present: "No", vendor_count: "",
  budget: "", tickets_sold: "No", ticketing_support: "No", decor_brought: "No", early_access: "No", next_day_breakdown: "No",
  vision: "", agree_no_guarantee: false, agree_availability: false, agree_deposit: false,
};

const inputCls = "w-full bg-transparent border border-white/15 text-white placeholder-white/30 px-4 py-3 text-sm focus:outline-none focus:border-epic-gold transition-colors";
const labelCls = "block text-xs uppercase tracking-[0.15em] text-white/70 mb-2";

function Text({ label, name, value, onChange, type = "text", required, placeholder }) {
  return (
    <div>
      <label className={labelCls}>{label}{required && <span className="text-epic-gold"> *</span>}</label>
      <input data-testid={`booking-${name}`} type={type} value={value} placeholder={placeholder}
        onChange={(e) => onChange(name, e.target.value)} className={inputCls} />
    </div>
  );
}

function Sel({ label, name, value, onChange, options, required }) {
  return (
    <div>
      <label className={labelCls}>{label}{required && <span className="text-epic-gold"> *</span>}</label>
      <select data-testid={`booking-${name}`} value={value} onChange={(e) => onChange(name, e.target.value)}
        className={`${inputCls} appearance-none`}>
        <option value="" className="bg-epic-charcoal">Select…</option>
        {options.map((o) => <option key={o} value={o} className="bg-epic-charcoal">{o}</option>)}
      </select>
    </div>
  );
}

function Radio({ label, name, value, onChange, options }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button key={o} type="button" data-testid={`booking-${name}-${o.replace(/\s+/g, "-").toLowerCase()}`}
            onClick={() => onChange(name, o)}
            className={`px-4 py-2 text-sm border transition-colors ${value === o ? "bg-epic-gold text-epic-black border-epic-gold" : "text-white/70 border-white/15 hover:border-epic-gold"}`}>
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}

function Checks({ label, name, values, onToggle, options }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <div className="grid sm:grid-cols-2 gap-2">
        {options.map((o) => {
          const active = values.includes(o);
          return (
            <button key={o} type="button" data-testid={`booking-${name}-${o.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => onToggle(name, o)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm border text-left transition-colors ${active ? "border-epic-gold text-white bg-epic-gold/10" : "text-white/70 border-white/15 hover:border-epic-gold/50"}`}>
              <span className={`w-4 h-4 border flex items-center justify-center shrink-0 ${active ? "bg-epic-gold border-epic-gold" : "border-white/30"}`}>
                {active && <Check size={12} className="text-epic-black" />}
              </span>
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function BookingForm() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? { ...initial, ...JSON.parse(saved) } : initial;
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [data]);

  const set = (name, value) => setData((d) => ({ ...d, [name]: value }));
  const toggle = (name, value) =>
    setData((d) => ({ ...d, [name]: d[name].includes(value) ? d[name].filter((x) => x !== value) : [...d[name], value] }));

  const validateStep = () => {
    if (step === 0) {
      if (!data.full_name || !data.email || !data.phone) { toast.error("Please complete required contact fields."); return false; }
    }
    if (step === 1 && !data.event_type) { toast.error("Please select an event type."); return false; }
    if (step === 5 && !(data.agree_no_guarantee && data.agree_availability && data.agree_deposit)) {
      toast.error("Please acknowledge all terms to submit."); return false;
    }
    return true;
  };

  const next = () => { if (validateStep()) setStep((s) => Math.min(s + 1, 5)); };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const { data: res } = await api.post("/bookings", data);
      setDone(res);
      localStorage.removeItem(STORAGE_KEY);
      toast.success("Inquiry submitted successfully!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="booking" data-testid="booking-section" className="py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-[1fr_360px] gap-10 lg:gap-16 items-start">
          <Reveal>
            <Overline>Book The Venue</Overline>
            <h2 className="font-display text-4xl md:text-6xl text-white leading-none mt-4">Let's Build Your Experience</h2>
            <p className="text-white/60 mt-4 max-w-xl">
              Tell us about your event and a member of The Epic Center team will connect with you to discuss
              availability, pricing, and logistics.
            </p>

            {done ? (
              <div data-testid="booking-success" className="mt-10 bg-epic-charcoal border border-epic-gold/30 p-10 text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-epic-gold flex items-center justify-center">
                  <Check size={32} className="text-epic-black" />
                </div>
                <h3 className="font-display text-3xl text-white mt-6">Thank You For Your Inquiry</h3>
                <p className="text-white/60 mt-3 max-w-md mx-auto">
                  A member of The Epic Center team will review your request and contact you within 24–48 business hours.
                </p>
                {done.quote && (
                  <div data-testid="booking-quote" className="mt-6 inline-block border border-epic-gold/40 px-6 py-4">
                    <p className="text-xs uppercase tracking-[0.2em] text-white/50">Preliminary Estimate</p>
                    <p className="font-display text-3xl text-epic-gold mt-1">
                      ${done.quote.estimate_low.toLocaleString()} – ${done.quote.estimate_high.toLocaleString()}
                    </p>
                  </div>
                )}
                {done.date_conflict && (
                  <p className="text-amber-400 text-sm mt-4">Note: your preferred date may already be reserved — we'll confirm alternatives.</p>
                )}
                <p className="text-epic-gold mt-6 flex items-center justify-center gap-2 text-sm">
                  <Phone size={16} /> (205) 212-0399 · (205) 746-6466
                </p>
              </div>
            ) : (
              <div className="mt-10 bg-epic-charcoal border border-white/5 p-6 md:p-10">
                {/* Progress */}
                <div className="flex items-center justify-between mb-8">
                  {STEPS.map((s, i) => (
                    <div key={s} className="flex-1 flex items-center">
                      <div className="flex flex-col items-center">
                        <div className={`w-8 h-8 flex items-center justify-center text-xs font-bold border ${i <= step ? "bg-epic-gold text-epic-black border-epic-gold" : "text-white/40 border-white/20"}`}>
                          {i < step ? <Check size={14} /> : i + 1}
                        </div>
                        <span className={`hidden md:block mt-2 text-[0.6rem] uppercase tracking-wider ${i <= step ? "text-epic-gold" : "text-white/40"}`}>{s}</span>
                      </div>
                      {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-1 ${i < step ? "bg-epic-gold" : "bg-white/15"}`} />}
                    </div>
                  ))}
                </div>

                <div className="space-y-5">
                  {step === 0 && (
                    <>
                      <Text label="Full Name" name="full_name" value={data.full_name} onChange={set} required />
                      <Text label="Business / Organization (optional)" name="organization" value={data.organization} onChange={set} />
                      <div className="grid sm:grid-cols-2 gap-5">
                        <Text label="Email Address" name="email" type="email" value={data.email} onChange={set} required />
                        <Text label="Phone Number" name="phone" type="tel" value={data.phone} onChange={set} required />
                      </div>
                      <Radio label="Preferred Method of Contact" name="contact_method" value={data.contact_method} onChange={set} options={["Phone", "Email", "Text"]} />
                      <Sel label="How did you hear about us?" name="referral_source" value={data.referral_source} onChange={set} options={["Social Media", "Google", "Referral", "Website", "Walk-In", "Other"]} />
                    </>
                  )}

                  {step === 1 && (
                    <>
                      <Sel label="What type of event are you hosting?" name="event_type" value={data.event_type} onChange={set} required
                        options={["Wedding Reception", "Baby Shower", "Bridal Shower", "Birthday Party", "Corporate Event", "Conference", "Workshop", "Repast", "Brunch", "Concert", "Comedy Show", "Pop-Up Shop", "Private Party", "Community Event", "Other"]} />
                      <Radio label="Is your event public or private?" name="event_visibility" value={data.event_visibility} onChange={set} options={["Public", "Private"]} />
                      <div className="grid sm:grid-cols-2 gap-5">
                        <Text label="Event Date Requested" name="event_date" type="date" value={data.event_date} onChange={set} />
                        <Text label="Alternate Date (optional)" name="alternate_date" type="date" value={data.alternate_date} onChange={set} />
                      </div>
                      <div className="grid sm:grid-cols-3 gap-5">
                        <Text label="Start Time" name="start_time" type="time" value={data.start_time} onChange={set} />
                        <Text label="End Time" name="end_time" type="time" value={data.end_time} onChange={set} />
                        <Text label="Guest Arrival" name="arrival_time" type="time" value={data.arrival_time} onChange={set} />
                      </div>
                      <Sel label="Estimated Guest Count" name="guest_count" value={data.guest_count} onChange={set} options={["Under 50", "50–100", "100–250", "250–500", "500–750"]} />
                    </>
                  )}

                  {step === 2 && (
                    <>
                      <Checks label="Preferred Room" name="preferred_rooms" values={data.preferred_rooms} onToggle={toggle} options={["Global Room", "City Room", "Entire Venue", "Unsure"]} />
                      <Sel label="Room Setup Style" name="setup_style" value={data.setup_style} onChange={set} options={["Banquet", "Theater", "Cocktail", "Classroom", "Lounge Style", "Standing Room Only", "Custom"]} />
                      <Radio label="Will you need stage access?" name="needs_stage" value={data.needs_stage} onChange={set} options={["Yes", "No"]} />
                      {data.needs_stage === "Yes" && (
                        <Sel label="Stage will be used for" name="stage_use" value={data.stage_use} onChange={set} options={["Performance", "Speaking", "Panel", "DJ", "Other"]} />
                      )}
                      <div className="grid sm:grid-cols-2 gap-5">
                        <Radio label="Need prep kitchen access?" name="needs_kitchen" value={data.needs_kitchen} onChange={set} options={["Yes", "No"]} />
                        <Radio label="Need prep space?" name="needs_prep" value={data.needs_prep} onChange={set} options={["Yes", "No"]} />
                      </div>
                      <Radio label="Will alcohol be served?" name="alcohol_served" value={data.alcohol_served} onChange={set} options={["Yes", "No", "Undecided"]} />
                      {data.alcohol_served === "Yes" && (
                        <Sel label="Alcohol service type" name="alcohol_type" value={data.alcohol_type} onChange={set} options={["Cash Bar", "Open Bar", "BYOB", "Licensed Vendor"]} />
                      )}
                    </>
                  )}

                  {step === 3 && (
                    <>
                      <Checks label="Do you need any of the following?" name="production_needs" values={data.production_needs} onToggle={toggle}
                        options={["Sound System", "Wireless Microphones", "Projector", "Video Screen", "Stage Lighting", "Uplighting", "DJ Booth", "Vendor Tables", "Pipe & Drape", "Security", "Cleaning Services", "Event Staffing"]} />
                      <Radio label="Will there be live entertainment?" name="live_entertainment" value={data.live_entertainment} onChange={set} options={["Yes", "No"]} />
                      {data.live_entertainment === "Yes" && (
                        <Sel label="Entertainment type" name="entertainment_type" value={data.entertainment_type} onChange={set} options={["Band", "DJ", "Comedian", "Speaker", "Performer"]} />
                      )}
                      <Radio label="Will vendors be present?" name="vendors_present" value={data.vendors_present} onChange={set} options={["Yes", "No"]} />
                      {data.vendors_present === "Yes" && (
                        <Text label="How many vendors?" name="vendor_count" type="number" value={data.vendor_count} onChange={set} />
                      )}
                    </>
                  )}

                  {step === 4 && (
                    <>
                      <Sel label="Estimated Budget" name="budget" value={data.budget} onChange={set} options={["Under $1,000", "$1,000–$2,500", "$2,500–$5,000", "$5,000–$10,000", "$10,000+"]} />
                      <div className="grid sm:grid-cols-2 gap-5">
                        <Radio label="Will tickets be sold?" name="tickets_sold" value={data.tickets_sold} onChange={set} options={["Yes", "No"]} />
                        <Radio label="Need ticketing support?" name="ticketing_support" value={data.ticketing_support} onChange={set} options={["Yes", "No"]} />
                      </div>
                      <div className="grid sm:grid-cols-2 gap-5">
                        <Radio label="Décor/setup brought in?" name="decor_brought" value={data.decor_brought} onChange={set} options={["Yes", "No"]} />
                        <Radio label="Need early access for setup?" name="early_access" value={data.early_access} onChange={set} options={["Yes", "No"]} />
                      </div>
                      <Radio label="Need next-day breakdown?" name="next_day_breakdown" value={data.next_day_breakdown} onChange={set} options={["Yes", "No"]} />
                    </>
                  )}

                  {step === 5 && (
                    <>
                      <div>
                        <label className={labelCls}>Tell us about your event vision</label>
                        <textarea data-testid="booking-vision" value={data.vision} onChange={(e) => set("vision", e.target.value)} rows={5}
                          placeholder="Theme, purpose, atmosphere, important details, special requests…" className={inputCls} />
                      </div>
                      {[
                        ["agree_no_guarantee", "I understand submission does not guarantee booking."],
                        ["agree_availability", "I understand dates are subject to availability."],
                        ["agree_deposit", "I understand a deposit may be required to secure my date."],
                      ].map(([key, text]) => (
                        <button key={key} type="button" data-testid={`booking-${key}`} onClick={() => set(key, !data[key])}
                          className="flex items-center gap-3 text-left text-sm text-white/80 w-full">
                          <span className={`w-5 h-5 border flex items-center justify-center shrink-0 ${data[key] ? "bg-epic-gold border-epic-gold" : "border-white/30"}`}>
                            {data[key] && <Check size={13} className="text-epic-black" />}
                          </span>
                          {text}
                        </button>
                      ))}
                    </>
                  )}
                </div>

                {/* Nav buttons */}
                <div className="flex items-center justify-between mt-9 pt-6 border-t border-white/5">
                  <button data-testid="booking-back" onClick={back} disabled={step === 0}
                    className="flex items-center gap-2 text-white/60 hover:text-white text-sm uppercase tracking-widest disabled:opacity-30">
                    <ChevronLeft size={16} /> Back
                  </button>
                  {step < 5 ? (
                    <button data-testid="booking-next" onClick={next}
                      className="flex items-center gap-2 bg-epic-gold text-epic-black font-bold uppercase tracking-widest text-sm px-8 py-3.5 hover:bg-epic-goldhover transition-colors">
                      Continue <ChevronRight size={16} />
                    </button>
                  ) : (
                    <button data-testid="booking-submit" onClick={submit} disabled={submitting}
                      className="bg-epic-gold text-epic-black font-bold uppercase tracking-widest text-sm px-8 py-3.5 hover:bg-epic-goldhover transition-colors disabled:opacity-60">
                      {submitting ? "Submitting…" : "Submit Booking Inquiry"}
                    </button>
                  )}
                </div>
              </div>
            )}
          </Reveal>

          <Reveal delay={0.15} className="hidden lg:block sticky top-28">
            <img src={BOOKING_IMG} alt="The Epic Center" className="w-full aspect-[3/4] object-cover" />
            <div className="bg-epic-charcoal border border-white/5 p-6 mt-4">
              <p className="text-xs uppercase tracking-[0.2em] text-epic-gold">Prefer to talk?</p>
              <p className="text-white mt-2 text-sm">Schedule a tour and walk the space in person.</p>
              <a href="tel:2052120399" data-testid="schedule-tour" className="mt-4 block text-center border border-epic-gold text-epic-gold hover:bg-epic-gold hover:text-epic-black font-bold uppercase tracking-widest text-xs px-6 py-3 transition-colors">
                Schedule A Tour
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
